<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { readPasteEvent } from "@/utils/pasteContext";
import ContextDetails from "./ContextDetails.vue";
import ContextSources from "./ContextSources.vue";
import ContextOptions from "./ContextOptions.vue";

// What the genie should read before writing: past messages, screenshots, files and
// pages, so nobody has to type it all out. A short note covers anything else, and the
// switches set how the genie works. Paste or drop anywhere on the tab.
const store = useStore();

const prompt = computed(() => store.state.prompt);
const contextQuestions = computed(
  () => store.state.questions.contextQuestionList.length
);
const contextQuestionLoading = computed(
  () => store.state.questions.contextQuestionLoading
);
const hasContext = computed(
  () =>
    store.state.context.context.trim().length > 5 ||
    store.state.context.documents.length > 0 ||
    store.state.context.images.length > 0
);

const openQuestions = () => {
  const tab = store.state.tabs.tabList.find((tab) => tab.name === "Questions");
  if (tab) store.dispatch("tabs/setTab", tab);
};

// Counting enter/leave keeps the highlight steady while dragging over child elements
const dragDepth = ref(0);
const hasFiles = (event) =>
  [...(event.dataTransfer?.types || [])].includes("Files");
const onDragEnter = (event) => {
  if (hasFiles(event)) dragDepth.value += 1;
};
const onDragLeave = (event) => {
  if (hasFiles(event)) dragDepth.value = Math.max(0, dragDepth.value - 1);
};
// Pasting outside a text box adds what was copied: a screenshot, an email thread...
const onPaste = (event) => {
  if (event.target.closest("input, textarea, [contenteditable]")) return;
  const pasted = readPasteEvent(event);
  if (!pasted.files.length && !pasted.text.trim()) return;
  event.preventDefault();
  store.dispatch("context/addPasted", pasted);
};

const onDrop = (event) => {
  dragDepth.value = 0;
  const files = [...(event.dataTransfer?.files || [])];
  if (files.length) store.dispatch("context/uploadDocuments", files);
};
</script>

<template>
  <div
    class="relative space-y-4"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
    @paste="onPaste"
  >
    <header>
      <h2 class="text-base font-bold text-primary sm:text-lg">
        Give the genie something to read
      </h2>
      <p class="text-sm text-secondary">
        Past emails, chats, notes or files<span class="hidden sm:inline">
          about your <span class="font-bold">{{ prompt || "make" }}</span></span
        >. It reads them so you don't have to explain.
      </p>
    </header>

    <ContextSources />
    <ContextDetails />

    <button
      v-if="hasContext && (contextQuestions || contextQuestionLoading)"
      type="button"
      class="flex w-full items-center justify-between gap-3 rounded-2xl bg-accentSecondaryLight px-4 py-3 text-left text-sm text-primary transition-colors hover:bg-yellow-100"
      @click="openQuestions"
    >
      <span class="flex items-center">
        <font-awesome-icon :icon="['fas', 'list-check']" class="mr-3 text-lg" />
        <span v-if="contextQuestions">
          The genie has
          <span class="font-bold"
            >{{ contextQuestions }} question{{
              contextQuestions === 1 ? "" : "s"
            }}</span
          >
          about what you added
        </span>
        <span v-else>Reading what you added for questions to ask...</span>
      </span>
      <span class="flex shrink-0 items-center font-bold">
        Questions
        <font-awesome-icon :icon="['fas', 'chevron-right']" class="ml-1.5" />
      </span>
    </button>

    <ContextOptions />

    <div
      v-if="dragDepth"
      class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-accentPrimary bg-white/90"
    >
      <p class="flex items-center text-lg font-bold text-accentPrimary">
        <font-awesome-icon :icon="['fas', 'paperclip']" class="mr-2" />
        Drop to add
      </p>
    </div>
  </div>
</template>
